<template>
  <Modal :show="showModal" title="Location Access Required" size="lg" @close="$emit('close')" :backdropExits="false">
    <div class="p-6">
      <!-- Status Illustration -->
      <div class="flex justify-center mb-6">
        <div class="relative">
          <div class="w-24 h-24 rounded-full bg-blue-50 flex items-center justify-center">
            <Icon v-if="permissionStatus === 'prompt'" name="ph:map-pin" class="text-blue-500" size="40" />
            <Icon v-else-if="permissionStatus === 'granted'" name="ph:check-circle" class="text-green-500" size="40" />
            <Icon v-else name="ph:warning" class="text-red-500" size="40" />
          </div>
          <div v-if="permissionStatus === 'granted'" class="absolute -top-1 -right-1">
            <span class="flex h-4 w-4">
              <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span class="relative inline-flex rounded-full h-4 w-4 bg-green-500"></span>
            </span>
          </div>
        </div>
      </div>

      <!-- Status Message -->
      <div class="text-center mb-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-2">
          {{ statusTitle }}
        </h3>
        <p class="text-gray-600">
          {{ statusMessage }}
        </p>
      </div>

      <!-- Features List -->
      <div v-if="permissionStatus !== 'granted'" class="bg-gray-50 rounded-xl p-4 mb-6">
        <h4 class="text-sm font-medium text-gray-700 mb-3">Why we need your location:</h4>
        <ul class="space-y-3">
          <li v-for="(feature, index) in features" :key="index" class="flex items-start gap-3">
            <div class="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5">
              <Icon :name="feature.icon" class="text-blue-600" size="14" />
            </div>
            <div>
              <span class="text-sm text-gray-600">{{ feature.text }}</span>
            </div>
          </li>
        </ul>
      </div>

      <!-- Browser Instructions -->
      <div v-if="permissionStatus === 'denied'" class="bg-red-50 rounded-xl p-4 mb-6">
        <h4 class="text-sm font-medium text-red-800 mb-2">How to enable location access:</h4>
        <ol class="list-decimal list-inside space-y-2 text-sm text-red-700">
          <li>Click the lock/info icon in your browser's address bar</li>
          <li>Find "Location" or "Site Settings"</li>
          <li>Change the permission to "Allow"</li>
          <li>Refresh this page</li>
        </ol>
      </div>

      <!-- Action Buttons -->
      <div class="flex justify-end gap-3">
        <button
          v-if="permissionStatus === 'denied' || permissionStatus === 'prompt'"
          @click="requestPermission"
          class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
          {{ permissionStatus === "denied" ? "Allow Location Access" : "Request Location Access" }}
        </button>
        <button v-if="permissionStatus === 'denied'" @click="refreshPage" class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
          Refresh Page
        </button>
        <button v-if="permissionStatus === 'granted'" @click="continueButton" class="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
          Continue
        </button>
      </div>
    </div>
  </Modal>
</template>

<script setup>
const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  permissionStatus: {
    type: String,
    required: true,
    validator: (value) => ["prompt", "granted", "denied"].includes(value),
  },
})

const showModal = computed({
  get: () => props.show,
  set: (value) => emit("update:show", value),
})

const emit = defineEmits(["request-permission", "close"])

const features = [
  {
    icon: "ph:map-pin",
    text: "Real-time location tracking for accurate delivery updates",
  },
  {
    icon: "ph:truck",
    text: "Efficient route optimization and navigation",
  },
  {
    icon: "ph:users",
    text: "Better coordination with customers and dispatch",
  },
  {
    icon: "ph:shield-check",
    text: "Enhanced safety and emergency response capabilities",
  },
]

const statusTitle = computed(() => {
  switch (props.permissionStatus) {
    case "prompt":
      return "Enable Location Services"
    case "granted":
      return "Location Access Enabled"
    case "denied":
      return "Location Access Required"
    default:
      return "Location Services"
  }
})

const statusMessage = computed(() => {
  switch (props.permissionStatus) {
    case "prompt":
      return "CargoLink needs access to your location to provide real-time tracking and navigation services."
    case "granted":
      return "Great! You can now use all location-based features of CargoLink."
    case "denied":
      return "Location access is currently blocked. Please enable it in your browser settings to continue."
    default:
      return "Please enable location services to use CargoLink."
  }
})

const requestPermission = () => {
  emit("request-permission")
}

const continueButton = async () => {
  refreshPage()
  emit("close")
}

const refreshPage = () => {
  window.location.reload()
}
</script>
